import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@truepill/tpos-react-router'
import {
  isOtcProductSplittable,
  isRxFillRequestSplittable,
  isSplitTargetLocation,
  isTriageResolvedBySplit,
  splitMayEmptyOriginal,
} from '@truepill/tpos-types'
import { ReactComponent as SplitIcon } from 'assets/icons/git-branch.svg'
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ButtonsContainer, InputContainer, ModalHeader, ModalWrapper } from 'components/Modal'
import { CancelButton } from 'components/PageStructure'
import Select from 'components/Select'
import type { Option } from 'components/Select'
import Lozenge, { getStatusColor } from 'components/Tiles/Lozenge'
import { SPLIT_ORDER } from 'gql'
import { CheckBox } from 'grommet'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useSplitWatch } from 'providers/SplitWatchProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import styled, { css } from 'styled-components'
import { bodyPrimaryColor, capsuleGreyColor, darkOrange, lightOrange } from 'styles/styleVariables'
import type { Order, TPOSLocation } from 'types'
import type { SplitChip } from 'utils/splitOrder'
import { leavesItemsOnOrder, splitChipForItem, splitItemIdsInFlight } from 'utils/splitOrder'

type SplitOrderModalProps = { order: Order }

// identifier and name sit in their own columns, so the identifiers line up as they do on the order page
type SplitItem = {
  id: string
  identifier: string
  name: string
  title: string
  status: string
  selectable: boolean
  chip?: SplitChip
}

const SplitOrderModal = ({ order }: SplitOrderModalProps): JSX.Element => {
  const { dismissModal } = useModalContext()
  const { locations, locationsById } = useTPCacheContext()
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()
  const { watchSplit } = useSplitWatch()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [targetLocationId, setTargetLocationId] = useState<string>(order.locationId)
  const [splitOrder, { loading }] = useMutation<{ splitOrder: { coreFillRequestToken: string } }>(SPLIT_ORDER, {
    refetchQueries: ['getFullOrder'],
  })

  const now = useMemo(() => new Date(), [])
  const inFlight = splitItemIdsInFlight(order, now)
  const items: SplitItem[] = [
    ...order.rxFillRequests.map(fill => {
      const splittable = isRxFillRequestSplittable(fill.status)
      const name = fill.fill?.dispensed?.name ?? fill.requestedMedicationName ?? ''
      return {
        id: fill._id,
        identifier: `RX: ${fill.prescription?.rxNumber ?? ''}`,
        name,
        title: name,
        status: fill.status,
        selectable: splittable && !inFlight.has(fill._id),
        chip: splitChipForItem(fill._id, order, splittable, now),
      }
    }),
    ...(order.otcProducts ?? []).map(otc => {
      const splittable = isOtcProductSplittable(otc.otcStatus)
      const name = otc.name ?? ''
      return {
        id: otc._id,
        identifier: 'OTC',
        name,
        // the SKU has no column of its own, so it rides in the tooltip
        title: `${name} \u00b7 SKU ${otc.sku}`,
        status: otc.otcStatus,
        selectable: splittable && !inFlight.has(otc._id),
        chip: splitChipForItem(otc._id, order, splittable, now),
      }
    }),
  ]

  const selectionHasRx = order.rxFillRequests.some(fill => selectedIds.has(fill._id))
  const shipToState = order.shippingAddress?.state
  const targets = locations
    .filter(location => isSplitTargetLocation(location, { selectionHasRx }))
    .sort((a, b) => a.name.localeCompare(b.name))
  // Core still runs compliance; this only spares the user a location that will bounce to Hayward
  const isUnlicensed = (location: TPOSLocation): boolean =>
    selectionHasRx &&
    !!shipToState &&
    !!location.licensedStates?.length &&
    !location.licensedStates.includes(shipToState)
  const targetOptions: Option<string>[] = targets.map(location =>
    isUnlicensed(location)
      ? {
          label: `${location.name} (not licensed for ${shipToState})`,
          value: location._id,
          additionalProps: { disabled: true },
        }
      : { label: location.name, value: location._id },
  )

  const targetIsAllowed = targets.some(location => location._id === targetLocationId && !isUnlicensed(location))
  // A target picked for an OTC-only selection may stop qualifying once an Rx joins the selection
  useEffect(() => {
    if (!targetIsAllowed && targetLocationId !== order.locationId) setTargetLocationId(order.locationId)
  }, [targetIsAllowed, targetLocationId, order.locationId])

  const resolvesTriages = (order.triages ?? []).some(isTriageResolvedBySplit)
  const emptiesOriginal = selectedIds.size > 0 && !leavesItemsOnOrder(order, selectedIds, now)
  const mayEmptyOriginal = splitMayEmptyOriginal({ selectionHasRx })
  const canSubmit = selectedIds.size > 0 && targetIsAllowed && (!emptiesOriginal || mayEmptyOriginal) && !loading
  const targetName = locationsById[targetLocationId]?.name ?? ''

  const toggle = (id: string, checked: boolean) =>
    setSelectedIds(previous => {
      const next = new Set(previous)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })

  const submit = async () => {
    if (!canSubmit) return
    try {
      const { data } = await splitOrder({
        variables: {
          orderId: order._id,
          locationId: targetLocationId,
          rxFillRequestIds: order.rxFillRequests.filter(fill => selectedIds.has(fill._id)).map(fill => fill._id),
          otcProductIds: (order.otcProducts ?? []).filter(otc => selectedIds.has(otc._id)).map(otc => otc._id),
        },
      })
      const coreFillRequestToken = data?.splitOrder?.coreFillRequestToken
      if (coreFillRequestToken) watchSplit({ orderId: order._id, coreFillRequestToken })
      showSuccessToast('New order being processed')
      dismissModal()
    } catch (error) {
      showErrorToast(`Failed to split order: ${(error as Error).message}`)
    }
  }

  return (
    <ModalWrapper id="SplitOrderModal" styles={modalWidth}>
      <ModalHeader>
        <IconWrapper>
          <SplitIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Split order</h2>
      </ModalHeader>
      <ItemList data-testid="split-items">
        {items.map(item => (
          <ItemRow key={item.id} data-testid="split-item" dimmed={!item.selectable}>
            <CheckBox
              disabled={!item.selectable}
              checked={selectedIds.has(item.id)}
              onChange={event => toggle(item.id, event.target.checked)}
            />
            <ItemIdentifier>{item.identifier}</ItemIdentifier>
            <ItemName title={item.title}>{item.name}</ItemName>
            <Lozenge backgroundColor={getStatusColor(item.status)}>{item.status}</Lozenge>
            {item.chip && (
              <Lozenge data-testid="split-chip" color={darkOrange} backgroundColor={lightOrange}>
                {item.chip.text}
              </Lozenge>
            )}
          </ItemRow>
        ))}
      </ItemList>
      <StyledInputContainer>
        <Select
          data-testid="split-target"
          modal
          fadePlaceholderText={false}
          hotKey="l"
          placeholder="Location"
          icon={<LocationIcon />}
          options={targetOptions}
          value={{ label: targetName, value: targetLocationId }}
          showActive={!!targetLocationId}
          disableClear
          onChange={([option]) => {
            if (option && !option.additionalProps?.disabled) setTargetLocationId(option.value as string)
          }}
        />
      </StyledInputContainer>
      {emptiesOriginal && (
        <Hint data-testid="split-hint">
          {mayEmptyOriginal
            ? 'The original order will be cancelled once these items move'
            : 'Select fewer items, or use Reroute to move the whole order'}
        </Hint>
      )}
      {resolvesTriages && (
        <Footnote data-testid="split-footnote">OOS triages will be removed from this order.</Footnote>
      )}
      <ButtonsContainer>
        <CancelButton label="Cancel" onClick={dismissModal} />
        <SaveButton
          disabled={!canSubmit}
          label={`Split ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}`}
          isModal
          onClick={submit}
        />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

// Long medication names truncate instead of widening the modal
const modalWidth = css`
  max-width: 36rem;
`

const ItemList = styled.ul`
  list-style: none;
  /* Scrolling clips both axes, so the rows are inset to leave room for the checkbox focus ring */
  margin: 0.25rem -0.25rem;
  padding: 0 0.25rem;
  max-height: 40vh;
  overflow-y: auto;
`

const ItemRow = styled.li<{ dimmed: boolean }>`
  display: grid;
  grid-template-columns: auto auto 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.375rem 0;
  opacity: ${({ dimmed }) => (dimmed ? 0.5 : 1)};
`

const ItemIdentifier = styled.span`
  white-space: nowrap;
`

const ItemName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledInputContainer = styled(InputContainer)`
  > button:first-child {
    height: 2.5rem;
  }
`

const Hint = styled.p`
  margin: 0 0 1rem;
  color: ${darkOrange};
`

const Footnote = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: ${capsuleGreyColor};
`

export default SplitOrderModal
